'use client';

import { type ReactNode, useCallback, useState, useRef } from 'react';
import { createSafeContext } from '@skin-seoul/utils';

interface CartDrawerContextValue {
  isOpen: boolean;
  onSetCartState: (state: boolean) => void;
  onCartOpen: () => void;
  onCartClose: () => void;
  onCartToggle: () => void;
}

const [CartDrawerProvider, useCartDrawerContext] = createSafeContext<CartDrawerContextValue>('CartDrawerContext');

interface CartDrawerProviderProps {
  children: ReactNode;
}

const RAPID_CLOSE_OPEN_THRESHOLD_MS = 500;

export function CartDrawerContextProvider({ children }: CartDrawerProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const lastCloseTimeRef = useRef<number>(0);

  const onSetCartState = useCallback((state: boolean) => {
    const timestamp = Date.now();

    // Prevent rapid open after close on mobile
    if (state === true) {
      const timeSinceLastClose = timestamp - lastCloseTimeRef.current;

      if (lastCloseTimeRef.current > 0 && timeSinceLastClose < RAPID_CLOSE_OPEN_THRESHOLD_MS) {
        return;
      }
    }

    if (state === false) {
      lastCloseTimeRef.current = timestamp;
    }

    setIsOpen(state);
  }, []);

  const onCartOpen = useCallback(() => {
    onSetCartState(true);
  }, [onSetCartState]);

  const onCartClose = useCallback(() => {
    onSetCartState(false);
  }, [onSetCartState]);

  const onCartToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  return (
    <CartDrawerProvider
      value={{
        isOpen,
        onSetCartState,
        onCartOpen,
        onCartClose,
        onCartToggle,
      }}
    >
      {children}
    </CartDrawerProvider>
  );
}

export const useCartDrawer = useCartDrawerContext;
